/**
 * 多くのコンポーネントから呼ばれるために、初期化コストがかかりはする。IOSのようにpluginにするかどうかは検討の余地ありだが、トータルでも数msくらい
 */
export const useI18n = () => {
  // console.time('useI18n')

  const route = useRoute()
  const isEn = computed(() => isEnPath(route.path))
  const isJa = computed(() => !isEn.value)
  const lang = computed(() => (isEn.value ? 'en' : 'ja'))

  /**
   * LangStringの言語に応じた出力
   * @param ls
   * @param fallback
   * @returns
   */
  const ls = (ls: LangString, fallback: string = ''): string => {
    if (!ls) return fallback
    if (ls[lang.value]) return ls[lang.value]
    if (ls.ja) return ls.ja
    return fallback
  }

  const lst = (langString: LangString, ...vars: any[]): string => {
    if (!langString) return ''
    let template: string = ls(langString)
    if (template) {
      for (let i = 0; i < vars.length; i++) {
        template = template.replace(/\{\}/, vars[i])
      }
      return template
    }
  }
  /**
   * 言語設定に応じて日本語か英語を返す
   *
   * @param ja 日本語
   * @param en 英語
   * @returns
   */
  const lss = (ja: string, en?: string): string => {
    return isEn.value && en != null ? en : ja
  }

  const lsst = (ja: string, en: string, ...vars: any[]): string => {
    return lst({ ja, en }, ...vars)
  }

  const li = (item: Item): string => {
    if (isJa.value) return item.common.title
    else return item.common.titleEn || item.common.title
  }

  const lid = (item: Item): string => {
    if (isJa.value) return escapeTag(item.common.description)
    else return escapeTag(item.common.descriptionEn || item.common.description)
  }

  //Enum値
  const led = (enumDef: EnumDef, code: string): string => {
    if (!enumDef) return '[NoEnumDef]'
    if (!enumDef.c[code]) return '[NoEnumCode:' + code + ']'
    return enumDef.c[code].l[lang.value]
  }

  //コード値
  const lc = (code: Code, key: string, fallback: string = ''): string => {
    if (!code) return fallback
    let value = code.values.filter(c => c.code === key)
    if (value.length > 0) return value[0].value[lang.value]
    return fallback
  }

  /**
   * コード値のDescription
   */
  const lcdesc = (code: Code, key: string): string => {
    if (!code) return ''
    let value = code.values.filter(c => c.code === key)
    if (value.length > 0) {
      return ls(value[0].description)
    }
    return ''
  }

  const lcbrief = (code: Code, key: string): string => {
    if (!code) return ''
    let value = code.values.filter(c => c.code === key)
    if (value.length > 0) {
      return ls(value[0]['brief'])
    }
    return ''
  }

  const ljoin = (arr: string[] | string): string => {
    if (arr && Array.isArray(arr)) return arr.join(isJa.value ? '、' : ', ')
    else return <string>arr
  }

  const ljoinNewLine = (arr: string[] | string): string => {
    if (arr && Array.isArray(arr)) return arr.join('\n')
    else return <string>arr
  }

  const ljoinLists = (...lists: string[][]): string => {
    const ret = []
    lists.forEach(list => {
      if (list) {
        const listStr = list.filter(e => !isEmptyString(e)).join(', ')
        if (!isEmptyString(listStr)) {
          ret.push(listStr)
        }
      }
    })
    return ret.join(' / ')
  }

  const langDelta = (d: LangDelta) => {
    if (isJa.value) {
      return d.ja
    } else {
      if (isEmptyDelta(d.en)) {
        return d.ja
      } else {
        return d.en
      }
    }
  }

  const ldate = (date: number): string => {
    if (!date) return ''
    let dateObject = new Date(date)
    if (isJa.value) {
      return (
        dateObject.getFullYear() +
        '/' +
        padding(dateObject.getMonth() + 1, 2) +
        '/' +
        padding(dateObject.getDate(), 2)
      )
    } else {
      return dateObject.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    }
  }

  const num = (n: number): string => {
    if (n != null) return n.toLocaleString()
    return ''
  }

  const nums = (n: number): string => {
    if (n == null) return ''
    if (isJa.value) {
      return shortNumberJa(n)
    } else {
      return shortNumberEn(n)
    }
  }

  const langNavigateTo = (to: any, options?: any) => {
    if (typeof to === 'string') {
      return navigateTo(getLangPath(isEn.value, to), options)
    } else {
      to.path = getLangPath(isEn.value, to.path)
      return navigateTo(to, options)
    }
  }

  const switchLangUrl = computed(() => {
    const query = toQueryString(getQueryMapFromRoute(route))
    let endpoint = getNonLangPath(
      query.length === 1 ? route.path : route.path + query,
    )
    if (!isEn.value && endpoint.endsWith('/')) endpoint = endpoint.slice(0, -1)
    return isEn.value ? endpoint : '/en' + endpoint
  })

  const switchLang = async () => {
    await navigateTo(switchLangUrl.value)
  }

  const isTopFunc = (path: string) => {
    const npath = normalizePath(path)
    return npath === '/' || npath === '/en'
  }

  const isTop = computed(() => {
    return isTopFunc(route.path)
  })

  const langPath = (path: string) => {
    return getLangPath(isEn.value, path)
  }

  const langFullPath = (path: string) => {
    return getFullPath(getLangPath(isEn.value, path))
  }

  const langUrl = (url: string) => {
    if (!url) return url
    if (!isEn.value) {
      return url
    }
    if (url.match(/^https?:\/\/dl\.ndl\.go\.jp\/ja\/.*/)) {
      return url.replace('/ja/', '/en/')
    }
    return url
  }

  const ld = (d: LangDelta) => {
    if (isJa.value) {
      return d.ja
    } else {
      if (isEmptyDelta(d.en)) {
        return d.ja
      } else {
        return d.en
      }
    }
  }

  // console.timeEnd('useI18n')
  return {
    isEn,
    isJa,
    ls,
    lst,
    lss,
    lsst,
    li,
    lid,
    led,
    num,
    nums,
    ldate,
    langDelta,
    ljoin,
    ljoinNewLine,
    ljoinLists,
    ld,
    lc,
    langPath,
    langFullPath,
    langUrl,
    langNavigateTo,
    switchLang,
    lcdesc,
    lcbrief,
    switchLangUrl,
    isTop,
    isTopFunc,
  }
}
